import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { BadgeCheck, CircleDashed, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { inr } from "@/lib/calc";
import { useAuth } from "@/lib/auth";
import { linesOf, verifyCount, paymentStateLabel } from "@/lib/payments";

const when = (iso) => new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
const dmy = (d) => (d ? d.split("-").reverse().join("/") : "");

export const VerifyToggle = ({ r, line, testid }) => {
  const { isAccountant } = useAuth();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const v = line.verified;
  const toggle = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/receipts/${r.id}/verify-payment`, { ...line.src, verified: !v });
      qc.setQueryData(["receipt", r.id], data);
      qc.setQueryData(["receipts"], (old) => old?.map((x) => (x.id === r.id ? data : x)));
      toast.success(v ? "Marked as not verified" : "Payment verified");
    } catch (e) { toast.error(e?.response?.data?.detail?.toString?.() || "Could not update"); }
    setBusy(false);
  };
  const label = v ? `Verified by ${v.by} · ${when(v.at)}` : "Not verified";
  const cls = `inline-flex min-w-[112px] items-center justify-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors duration-150 ${v ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-amber-300 bg-amber-50 text-amber-900"}`;
  const icon = busy ? <Loader2 className="h-3 w-3 animate-spin" /> : v ? <BadgeCheck className="h-3 w-3" /> : <CircleDashed className="h-3 w-3" />;
  if (!isAccountant) return <span className={cls} title={label} data-testid={testid} data-verified={v ? "true" : "false"}>{icon}{v ? "Verified" : "Not verified"}</span>;
  return (
    <button type="button" onClick={toggle} disabled={busy} title={v ? `${label} — click to untick` : "Click to mark as checked against statement"}
      className={`${cls} hover:border-emerald-400`} data-testid={testid} data-verified={v ? "true" : "false"}>
      {icon}{v ? "Verified" : "Mark verified"}
    </button>
  );
};

export const LateTag = ({ l, testid }) => (l.late_days > 0 ? (
  <span className={`ml-1.5 inline-block rounded border px-1.5 py-0.5 text-[10px] font-semibold ${l.late_days === 1 ? "border-sky-300 bg-sky-50 text-sky-800" : "border-amber-300 bg-amber-50 text-amber-900"}`}
    title={l.late_reason ? `Reason: ${l.late_reason}` : ""} data-testid={testid}>
    {l.late_days === 1 ? "Recorded next day" : `Recorded ${l.late_days} days late`}{l.late_reason ? ` - ${l.late_reason}` : ""}
  </span>
) : null);

export const PaymentState = ({ r, testid }) => {
  const c = verifyCount(r);
  if (!c.total) return <span className="text-slate-400" data-testid={testid}>—</span>;
  const cls = c.n === c.total ? "border-emerald-300 bg-emerald-50 text-emerald-800" : c.n === 0 ? "border-amber-300 bg-amber-50 text-amber-900" : "border-sky-300 bg-sky-50 text-sky-800";
  const Icon = c.n === c.total ? BadgeCheck : CircleDashed;
  return <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-medium ${cls}`} data-testid={testid}><Icon className="h-3 w-3" />{paymentStateLabel(c)}</span>;
};

export const PaymentChecks = ({ r }) => {
  const lines = linesOf(r);
  if (!lines.length) return null;
  return (
    <div className="no-print mb-4 rounded-lg border border-plum-900/10 bg-white p-4" data-testid="payment-checks">
      <div className="mb-2 text-[11px] uppercase tracking-wider text-slate-500">Payments &amp; accounts verification</div>
      <ul className="divide-y divide-plum-900/5 text-sm">
        {lines.map((l, i) => (
          <li key={i} className="flex flex-wrap items-center justify-between gap-2 py-2" data-testid={`payment-check-${i}`}>
            <span><b className="text-plum-950">{l.kind}</b> · ₹ {inr(l.amount)} · {l.mode}{l.reference ? ` (${l.reference})` : ""} · {dmy(l.date)}
              <LateTag l={l} testid={`payment-late-${i}`} /></span>
            <span className="flex items-center gap-2">
              {l.verified && <span className="text-[11px] text-slate-500" data-testid={`payment-check-by-${i}`}>{l.verified.by} · {when(l.verified.at)}</span>}
              <VerifyToggle r={r} line={l} testid={`payment-verify-${i}`} />
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};
